import React, { useState } from 'react';
import { SocialLink } from '../../../services/api';

interface SocialLinksEditorProps {
  initialLinks: SocialLink[];
  onSave: (links: SocialLink[]) => Promise<{ success: boolean; error?: string }>;
}

const SocialLinksEditor: React.FC<SocialLinksEditorProps> = ({ initialLinks, onSave }) => {
  const [links, setLinks] = useState<SocialLink[]>(initialLinks);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [newPlatform, setNewPlatform] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const platformOptions = [
    'Instagram',
    'Facebook',
    'Twitter',
    'LinkedIn',
    'Pinterest',
    'YouTube',
    'TikTok',
    'Behance',
    'Dribbble',
    'ArtStation',
    'DeviantArt',
    'Website',
    'Other'
  ];

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const result = await onSave(links);
      
      if (result.success) {
        setSuccess(true);
        setIsEditing(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to save social links');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLinks(initialLinks);
    setIsEditing(false);
    setError(null);
  };

  const addLink = () => {
    if (!newPlatform || !newUrl) return;
    
    const newLink: SocialLink = {
      platform: newPlatform,
      url: newUrl
    };
    
    setLinks([...links, newLink]);
    setNewPlatform('');
    setNewUrl('');
  };

  const removeLink = (index: number) => {
    const updatedLinks = [...links];
    updatedLinks.splice(index, 1);
    setLinks(updatedLinks);
  };

  const updateLink = (index: number, field: keyof SocialLink, value: string) => {
    const updatedLinks = [...links];
    updatedLinks[index] = { 
      ...updatedLinks[index], 
      [field]: value 
    };
    setLinks(updatedLinks);
  };

  const getPlatformIcon = (platform: string) => {
    const lowerPlatform = platform.toLowerCase();
    
    switch (lowerPlatform) {
      case 'instagram':
        return '📸';
      case 'facebook':
        return '👤';
      case 'twitter':
        return '🐦';
      case 'linkedin':
        return '💼';
      case 'pinterest':
        return '📌';
      case 'youtube':
        return '📹';
      case 'tiktok':
        return '🎵';
      case 'behance':
        return '🎨';
      case 'dribbble':
        return '🏀';
      case 'artstation':
        return '🖼️';
      case 'deviantart':
        return '🎭';
      case 'website':
        return '🌐';
      default:
        return '🔗';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-medium text-gray-800">Social Media Links</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Edit Links
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-md overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-2 px-4 text-left">Platform</th>
                  <th className="py-2 px-4 text-left">URL</th>
                  <th className="py-2 px-4 text-center w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {links.map((link, index) => (
                  <tr key={index} className="border-t border-gray-200">
                    <td className="py-2 px-4">
                      <label htmlFor={`platform-${index}`} className="sr-only">Platform for link {index + 1}</label>
                      <select
                        id={`platform-${index}`}
                        value={link.platform}
                        onChange={(e) => updateLink(index, 'platform', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                        aria-label={`Platform for link ${index + 1}`}
                      >
                        {platformOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-4">
                      <label htmlFor={`url-${index}`} className="sr-only">URL for link {index + 1}</label>
                      <input
                        id={`url-${index}`}
                        type="url"
                        value={link.url}
                        onChange={(e) => updateLink(index, 'url', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder="https://..."
                        aria-label={`URL for link ${index + 1}`}
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <button
                        onClick={() => removeLink(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-gray-200">
                  <td className="py-2 px-4">
                    <label htmlFor="new-platform" className="sr-only">New platform</label>
                    <select
                      id="new-platform"
                      value={newPlatform}
                      onChange={(e) => setNewPlatform(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                      aria-label="New platform"
                    >
                      <option value="">Select platform...</option>
                      {platformOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-4">
                    <label htmlFor="new-url" className="sr-only">New URL</label>
                    <input
                      id="new-url"
                      type="url"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="https://..."
                      aria-label="New URL"
                    />
                  </td>
                  <td className="py-2 px-4 text-center">
                    <button
                      onClick={addLink}
                      disabled={!newPlatform || !newUrl}
                      className="text-primary hover:text-primary-dark disabled:text-gray-400"
                    >
                      Add
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Links'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
          
          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="text-green-500 text-sm">
              Social links saved successfully!
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 p-4 rounded-md">
          {links.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <span>{getPlatformIcon(link.platform)}</span>
                  <span>{link.platform}</span>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No social links have been added yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SocialLinksEditor;